import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import PreferredCaregiversPanel, {
  MAX_CAREGIVER_PREFERENCES,
} from "../marketplace/PreferredCaregiversPanel";
import {
  readStoredCaregiverPreferences,
  readViewedCaregivers,
  writeStoredCaregiverPreferences,
  preferenceIdsFromList,
} from "../../utils/caregiverPreferences";
import { showToast } from "../../utils/toast";
import { formatUserFacingError, notifyUserFacingError, USER_FACING_ERRORS } from "../../utils/userFacingErrors";
import {
  CUSTOMER_SIGNUP_STEPS,
  PRIMARY_DIAGNOSIS_OPTIONS,
  RELATIONSHIP_OPTIONS,
} from "../../config/customerOnboarding";
import {
  ELDER_RISK_QUESTIONS,
  INIT_ELDER_RISK_ANSWERS,
  buildRiskAssessment,
} from "../../config/elderRiskAssessment";
import {
  buildMarketplaceQueryFromElder,
  buildRelaxedMarketplaceQueryFromElder,
  rankCaregiversForElder,
} from "../../config/elderToMarketplaceMatch";
import { mapMarketplaceCaregiver } from "../../utils/mapMarketplaceCaregiver";
import { shouldShowCustomerAadhaarKycUi } from "../../config/customerAccessPolicy";
import ClinicalScreeningOutputModal from "./ClinicalScreeningOutputModal";
import {
  assertCaregiverAvailableOnDates,
  parseUnavailableCaregiverIds,
} from "../../utils/caregiverDateAvailability";
import "./CustomerHireOnboarding.css";

const INIT_SENIOR = {
  relationshipKey: "",
  relationshipOther: "",
  name: "",
  age: "",
  address: "",
  diagnosisKey: "",
  primaryDiagnosis: "",
  notes: "",
};

function resolveRelationshipToSenior(senior) {
  if (senior.relationshipKey === "other") return senior.relationshipOther.trim();
  const match = RELATIONSHIP_OPTIONS.find((o) => o.value === senior.relationshipKey);
  return match?.label || senior.relationshipKey || "";
}

function diagnosisLabel(key) {
  const match = PRIMARY_DIAGNOSIS_OPTIONS.find((o) => o.value === key);
  return match?.label || "";
}

function resolvePrimaryDiagnosis(senior) {
  if (senior.diagnosisKey === "other") return senior.primaryDiagnosis.trim();
  if (senior.diagnosisKey) return diagnosisLabel(senior.diagnosisKey);
  return senior.primaryDiagnosis.trim();
}

function SignupProgress({ current, total }) {
  return (
    <div className="hire-onboarding__progress" aria-label={`Step ${current} of ${total}`}>
      <span className="hire-onboarding__progress-label">
        STEP {current} OF {total}
      </span>
      <div
        className="hire-onboarding__progress-track"
        style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
        aria-hidden="true"
      >
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={`hire-onboarding__progress-seg${i < current ? " is-filled" : ""}`}
          />
        ))}
      </div>
    </div>
  );
}

function RiskWarningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.5L21.5 20H2.5L12 3.5z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path d="M12 10v4.5M12 17.5h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function RiskQuestionIcon({ type }) {
  if (type === "memory") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M9.5 8.5a2.5 2.5 0 115 0c0 1.5-1.2 2.2-1.8 2.8-.7.7-1.2 1.3-1.2 2.4"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <circle cx="12" cy="17.2" r="1" fill="currentColor" />
        <path
          d="M4.5 12a7.5 7.5 0 1112.8 5.3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (type === "mobility") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="5" r="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 7.5v5.5M9 21l3-8 3 8M8 13h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "medical") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "care") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 20s-7-4.2-7-9.2A3.8 3.8 0 0112 8a3.8 3.8 0 017 2.8C19 15.8 12 20 12 20z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // fall (default)
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="15.5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M14.5 8.2l-2.2 3.3-3.3-1.2M12.2 11.6L9 19.5M8.8 10.2L5.5 14"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M4 20.5h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export default function CustomerHireOnboarding({
  onComplete,
  searchContext = {},
  initialSenior = {},
  customerEmail = "",
  embedded = false,
  requireBooking = true,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(2);
  const [step2Phase, setStep2Phase] = useState("senior");
  const [locationEditable, setLocationEditable] = useState(false);
  const [careManagerAssigned, setCareManagerAssigned] = useState(null);
  const [senior, setSenior] = useState({
    ...INIT_SENIOR,
    ...initialSenior,
    diagnosisKey: initialSenior.diagnosisKey || "",
    relationshipKey: initialSenior.relationshipKey || "",
    relationshipOther: initialSenior.relationshipOther || "",
  });
  const [riskAnswers, setRiskAnswers] = useState(INIT_ELDER_RISK_ANSWERS);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [matchedCaregivers, setMatchedCaregivers] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [preferences, setPreferences] = useState(() => readStoredCaregiverPreferences());
  const viewedCaregivers = useMemo(() => readViewedCaregivers(), [showResultsModal]);
  const [busy, setBusy] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);
  const liveAssessment = useMemo(() => buildRiskAssessment(riskAnswers), [riskAnswers]);
  const isHighRiskCase = liveAssessment.highRisk;
  const suggestedCaregiver =
    isHighRiskCase && preferences.length === 0 ? matchedCaregivers[0] || null : null;

  const preferenceIds = useMemo(
    () => new Set(preferences.map((p) => String(p.id))),
    [preferences]
  );

  async function queryMarketplaceCaregivers(coords, filterParams) {
    let res;
    try {
      res = await api.getMarketplaceCaregivers({
        latitude: coords.latitude,
        longitude: coords.longitude,
        ...filterParams,
      });
    } catch (err) {
      if (err?.code === "CUSTOMER_KYC_REQUIRED" || err?.status === 403) {
        res = await api.getPublicMarketplaceCaregivers({
          latitude: coords.latitude,
          longitude: coords.longitude,
          ...filterParams,
        });
      } else {
        throw err;
      }
    }

    const payload = res?.data && typeof res.data === "object" ? res.data : res;
    const list = Array.isArray(payload?.qualified) ? payload.qualified : [];
    return list.map(mapMarketplaceCaregiver).filter(Boolean);
  }

  async function fetchConditionMatchedCaregivers(assessment) {
    const strict = buildMarketplaceQueryFromElder({ senior, assessment, searchContext });
    const coords =
      strict.coords?.latitude && strict.coords?.longitude
        ? strict.coords
        : { latitude: 12.9716, longitude: 77.5946 };

    try {
      let mapped = await queryMarketplaceCaregivers(coords, strict.filterParams);
      let ranked = rankCaregiversForElder(mapped, strict.criteria);

      if (!ranked.length) {
        const relaxed = buildRelaxedMarketplaceQueryFromElder({ senior, assessment, searchContext });
        mapped = await queryMarketplaceCaregivers(coords, relaxed.filterParams);
        ranked = rankCaregiversForElder(mapped, strict.criteria);
      }

      return ranked.slice(0, 2).map((caregiver) => ({
        ...caregiver,
        tags: caregiver.skills?.length ? caregiver.skills.slice(0, 3) : [],
      }));
    } catch {
      return [];
    }
  }

  function setRiskAnswer(id, value) {
    setRiskAnswers((prev) => ({ ...prev, [id]: value }));
  }

  function validateSeniorProfile() {
    if (!senior.relationshipKey) {
      showToast.error("Please select your relationship with the patient.");
      return false;
    }
    if (senior.relationshipKey === "other" && !senior.relationshipOther.trim()) {
      showToast.error("Please mention your relationship with the patient.");
      return false;
    }
    if (!senior.name.trim()) {
      showToast.error("Please enter the patient's full name.");
      return false;
    }
    if (!senior.age || Number(senior.age) < 1) {
      showToast.error("Please enter a valid age for the patient.");
      return false;
    }
    if (!senior.address.trim()) {
      showToast.error("Please enter the patient's address.");
      return false;
    }
    if (senior.diagnosisKey === "other" && !senior.primaryDiagnosis.trim()) {
      showToast.error("Please describe the primary medical condition.");
      return false;
    }
    return true;
  }

  function validateRiskAssessment() {
    const unanswered = ELDER_RISK_QUESTIONS.find((q) => riskAnswers[q.id] === null);
    if (unanswered) {
      showToast.error(`Please answer: ${unanswered.label}`);
      return false;
    }
    return true;
  }

  async function persistSeniorProfile({ assessment = null } = {}) {
    const conditions = resolvePrimaryDiagnosis(senior);
    const payload = {
      name: senior.name.trim(),
      age: Number(senior.age),
      address: senior.address.trim(),
      relationshipToSenior: resolveRelationshipToSenior(senior),
      primaryDiagnosis: conditions,
      notes: senior.notes.trim(),
      conditions,
    };
    if (assessment) {
      payload.flags = assessment.flags;
      payload.healthAssessment = {
        ...assessment.healthAssessment,
        highRisk: assessment.highRisk,
        riskScore: assessment.riskScore,
        riskLevel: assessment.riskLevel,
        riskAnswers: assessment.riskAnswers,
      };
    }
    await api.updateCustomerElderProfile(payload);
  }

  async function handleCreateDashboard() {
    if (!validateSeniorProfile()) return;
    setBusy(true);
    try {
      await persistSeniorProfile();
      // Risk assessment and remaining patient details are completed on the dashboard.
      if (onComplete) onComplete({});
      else navigate("/dashboard", { replace: true });
    } catch (err) {
      showToast.error(err.message || "Unable to create your dashboard.");
    } finally {
      setBusy(false);
    }
  }

  async function completeRiskAssessment({ skip = false } = {}) {
    if (!skip && !validateRiskAssessment()) return;
    setBusy(true);
    try {
      if (skip) {
        setStep(3);
        return;
      }
      const assessment = buildRiskAssessment(riskAnswers);
      await persistSeniorProfile({ assessment });
      setAssessmentResult(assessment);
      setMatchedCaregivers([]);
      setShowResultsModal(true);
      setMatchesLoading(true);
      try {
        const ranked = await fetchConditionMatchedCaregivers(assessment);
        setMatchedCaregivers(ranked);
      } catch (err) {
        showToast.error(err.message || "Unable to load matched caregivers.");
      } finally {
        setMatchesLoading(false);
      }
    } catch (err) {
      showToast.error(err.message || "Unable to save risk assessment.");
    } finally {
      setBusy(false);
    }
  }

  function proceedAfterAssessment() {
    setShowResultsModal(false);
    setStep(3);
  }

  function retakeAssessment() {
    setRiskAnswers(INIT_ELDER_RISK_ANSWERS);
    setAssessmentResult(null);
    setShowResultsModal(false);
    setStep2Phase("health");
  }

  function addPreference(c) {
    if (!c?.id || preferenceIds.has(String(c.id))) return;
    if (preferences.length >= MAX_CAREGIVER_PREFERENCES) {
      showToast.error(`You can select up to ${MAX_CAREGIVER_PREFERENCES} caregivers.`);
      return;
    }
    setPreferences((prev) => {
      const next = [...prev, c];
      writeStoredCaregiverPreferences(next);
      return next;
    });
  }

  function removePreference(id) {
    setPreferences((prev) => {
      const next = prev.filter((p) => String(p.id) !== String(id));
      writeStoredCaregiverPreferences(next);
      return next;
    });
  }

  function movePreference(from, to) {
    if (to < 0 || to >= preferences.length) return;
    setPreferences((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      writeStoredCaregiverPreferences(next);
      return next;
    });
  }

  async function confirmBooking() {
    if (!preferences.length) {
      showToast.error("Add at least one preferred caregiver.");
      return;
    }
    const primary = preferences[0];
    setCareManagerAssigned(null);
    setBusy(true);
    try {
      const serviceDate =
        searchContext.searchDate || new Date().toISOString().slice(0, 10);
      const prefIds = preferences.map((p) => p?.id).filter(Boolean);
      let availablePrefs = preferences;
      if (prefIds.length && /^\d{4}-\d{2}-\d{2}$/.test(String(serviceDate).slice(0, 10))) {
        try {
          const res = await api.getCaregiversUnavailableOnDate({
            serviceDate,
            caregiverIds: prefIds,
          });
          const blocked = parseUnavailableCaregiverIds(res);
          availablePrefs = preferences.filter((p) => !blocked.has(String(p?.id)));
        } catch {
          /* fall through — confirm API still enforces */
        }
      }
      if (!availablePrefs.length) {
        showToast.error(
          `No preferred caregivers are available on ${serviceDate}. Choose another date or add another caregiver.`
        );
        return;
      }
      const bookPrimary = availablePrefs[0];
      await assertCaregiverAvailableOnDates(api, {
        caregiverId: bookPrimary.id,
        serviceDates: [serviceDate],
        leaveOnly: true,
      });
      let patientId = searchContext.patientId || searchContext.patient_id || "";
      if (!patientId) {
        const patientsRes = await api.listCustomerPatients().catch(() => null);
        const list = patientsRes?.patients || patientsRes?.data?.patients || [];
        patientId =
          (list.find((p) => p.isPrimary)?.patientId || list[0]?.patientId || "").toString().trim();
      }
      if (!patientId) {
        showToast.error("Add a patient profile before booking.");
        navigate("/elder-profile");
        return;
      }
      const res = await api.confirmMarketplaceBooking({
        caregiverId: bookPrimary.id,
        serviceType: "FULL_TIME",
        date: serviceDate,
        caregiverPreferenceIds: preferenceIdsFromList(availablePrefs),
        shiftSchedule: searchContext.planType || "monthly",
        medicalNotes: senior.notes.trim() || undefined,
        consultationType: searchContext.consultationType || undefined,
        patientId,
        policeVerification: { deferred: true, option: "STANDARD_PRV" },
      });
      const bookingId = res?.booking?.id || res?.data?.booking?.id || res?.bookingId;
      setBookingRef(bookingId || "Pending");
      setCareManagerAssigned(Boolean(res?.careManagerAssigned));
      setStep(4);
    } catch (err) {
      if (err?.code === "CUSTOMER_KYC_REQUIRED") {
        const message = formatUserFacingError(err, USER_FACING_ERRORS.CUSTOMER_KYC_REQUIRED.message);
        showToast.info(message);
        navigate(shouldShowCustomerAadhaarKycUi() ? "/profile#aadhaar-kyc" : "/marketplace", {
          replace: true,
          state: { loginMessage: message },
        });
        return;
      }
      notifyUserFacingError(err, "Unable to submit booking request.");
    } finally {
      setBusy(false);
    }
  }

  function finishToDashboard() {
    if (onComplete) onComplete({ bookingId: bookingRef });
    else navigate("/dashboard", { replace: true, state: { bookingId: bookingRef } });
  }

  function finishSignupWithoutBooking() {
    if (onComplete) onComplete({});
    else navigate("/marketplace", { replace: true });
  }

  const rootClass = `hire-onboarding${embedded ? " hire-onboarding--embedded" : ""}${step === 4 ? " hire-onboarding--confirm" : ""}`;

  function renderRiskQuestion(question) {
    const value = riskAnswers[question.id];
    const legendId = `risk-q-${question.id}`;
    return (
      <fieldset key={question.id} className="hire-onboarding__risk-q" aria-labelledby={legendId}>
        <div className="hire-onboarding__risk-q-head" id={legendId}>
          <span className="hire-onboarding__risk-q-icon" aria-hidden="true">
            <RiskQuestionIcon type={question.icon} />
          </span>
          <span className="hire-onboarding__risk-q-title">{question.label}</span>
        </div>
        <div className="hire-onboarding__risk-options">
          {question.options.map((opt) => (
            <label
              key={opt.value}
              className={`hire-onboarding__risk-option${value === opt.value ? " is-selected" : ""}`}
            >
              <input
                type="radio"
                name={question.id}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => setRiskAnswer(question.id, opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (step === 2 && step2Phase === "senior") {
    const stepMeta = CUSTOMER_SIGNUP_STEPS.seniorProfile;
    return (
      <div className={rootClass}>
        <SignupProgress current={stepMeta.index} total={stepMeta.total} />
        <div className="hire-onboarding__head">
          <h2>{stepMeta.title}</h2>
          <p>
            Tell us about your loved one&apos;s care needs. This helps NEMA match the right support.
          </p>
        </div>
        <div className="hire-onboarding__form">
          <label>
            Your Relationship with patient <span className="hire-onboarding__required">*</span>
            <select
              value={senior.relationshipKey}
              onChange={(e) =>
                setSenior((s) => ({
                  ...s,
                  relationshipKey: e.target.value,
                  relationshipOther: e.target.value === "other" ? s.relationshipOther : "",
                }))
              }
            >
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {senior.relationshipKey === "other" ? (
            <label>
              Please mention <span className="hire-onboarding__required">*</span>
              <input
                value={senior.relationshipOther}
                onChange={(e) => setSenior((s) => ({ ...s, relationshipOther: e.target.value }))}
                placeholder="Describe your relationship"
              />
            </label>
          ) : null}
          <label>
            Full name of patient <span className="hire-onboarding__required">*</span>
            <input
              value={senior.name}
              onChange={(e) => setSenior((s) => ({ ...s, name: e.target.value }))}
              placeholder="Patient's full name"
              autoComplete="name"
            />
          </label>
          <div className="hire-onboarding__row">
            <label style={{ gridColumn: "1 / -1" }}>
              Age <span className="hire-onboarding__required">*</span>
              <input
                type="number"
                min="1"
                max="120"
                value={senior.age}
                onChange={(e) => setSenior((s) => ({ ...s, age: e.target.value }))}
                placeholder="Age"
              />
            </label>
          </div>
          <label>
            Service location <span className="hire-onboarding__required">*</span>
            <input
              value={senior.address}
              onChange={(e) => setSenior((s) => ({ ...s, address: e.target.value }))}
              placeholder="Service location where care is needed"
              autoComplete="street-address"
              readOnly={!locationEditable}
            />
          </label>
          {!locationEditable ? (
            <button
              type="button"
              className="hire-onboarding__change-location"
              onClick={() => setLocationEditable(true)}
              disabled={busy}
            >
              Change location
            </button>
          ) : null}
          <label>
            Primary diagnosis / medical condition
            <select
              value={senior.diagnosisKey}
              onChange={(e) =>
                setSenior((s) => ({
                  ...s,
                  diagnosisKey: e.target.value,
                  primaryDiagnosis: e.target.value === "other" ? s.primaryDiagnosis : "",
                }))
              }
            >
              {PRIMARY_DIAGNOSIS_OPTIONS.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {senior.diagnosisKey === "other" ? (
            <label>
              Describe condition
              <input
                value={senior.primaryDiagnosis}
                onChange={(e) => setSenior((s) => ({ ...s, primaryDiagnosis: e.target.value }))}
                placeholder="e.g. COPD, kidney disease"
              />
            </label>
          ) : null}
          <label>
            Additional notes or special instructions
            <textarea
              rows={3}
              value={senior.notes}
              onChange={(e) => setSenior((s) => ({ ...s, notes: e.target.value }))}
              placeholder="Diet preferences, mobility aids, routines, allergies…"
            />
          </label>
        </div>
        <button
          type="button"
          className="hire-onboarding__cta"
          disabled={busy}
          onClick={handleCreateDashboard}
        >
          {busy ? "Creating…" : "Create Your Dashboard"}
        </button>
      </div>
    );
  }

  if (step === 2 && step2Phase === "health") {
    const stepMeta = CUSTOMER_SIGNUP_STEPS.healthAssessment;
    return (
      <>
        <div className={rootClass}>
          <SignupProgress current={stepMeta.index} total={stepMeta.total} />
          <div className="hire-onboarding__head">
            <h2>{stepMeta.title}</h2>
            <p>
              Our clinical screening helps determine care needs and recommends the most suitable caregiver or Care
              Manager.
            </p>
            {isHighRiskCase ? (
              <div className="hire-onboarding__risk-notice" role="status">
                <span className="hire-onboarding__risk-notice-icon" aria-hidden="true">
                  <RiskWarningIcon />
                </span>
                <p>
                  <strong>High-risk case detected.</strong> A care manager will be prioritized for your booking
                  request. You can still continue using <strong>Skip</strong>.
                </p>
              </div>
            ) : null}
            <button
              type="button"
              className="hire-onboarding__skip hire-onboarding__skip--top"
              disabled={busy}
              onClick={() => completeRiskAssessment({ skip: true })}
            >
              Skip for now &gt;
            </button>
          </div>
          <div className="hire-onboarding__health-panel">
            {ELDER_RISK_QUESTIONS.map((q) => renderRiskQuestion(q))}
          </div>
          <div className="hire-onboarding__actions">
            <button
              type="button"
              className="hire-onboarding__back"
              disabled={busy}
              onClick={() => setStep2Phase("senior")}
            >
              Back
            </button>
            <button
              type="button"
              className="hire-onboarding__cta hire-onboarding__cta--inline"
              disabled={busy}
              onClick={() => completeRiskAssessment()}
            >
              {busy ? "Saving…" : "View Screening Result"}
            </button>
          </div>
        </div>
        <ClinicalScreeningOutputModal
          open={showResultsModal}
          seniorName={senior.name}
          assessment={assessmentResult}
          caregivers={matchedCaregivers}
          matchesLoading={matchesLoading}
          matchCriteria={
            assessmentResult
              ? buildMarketplaceQueryFromElder({
                  senior,
                  assessment: assessmentResult,
                  searchContext,
                }).criteria
              : null
          }
          customerEmail={customerEmail}
          onContinue={proceedAfterAssessment}
          onContinueAnyway={proceedAfterAssessment}
          onRetake={retakeAssessment}
          onAddCaregiver={addPreference}
          busy={busy}
        />
      </>
    );
  }

  if (step === 3) {
    const stepMeta = CUSTOMER_SIGNUP_STEPS.preferences;
    return (
      <div className={rootClass}>
        <SignupProgress current={stepMeta.index} total={stepMeta.total} />
        <div className="hire-onboarding__head">
          <h2>{stepMeta.title}</h2>
          <p>
            {requireBooking
              ? "Choose up to 3 caregivers. Your request will be sent in your order of preference."
              : "Optionally shortlist caregivers you liked. You can book from the marketplace after KYC."}
          </p>
        </div>
        {suggestedCaregiver ? (
          <div className="hire-onboarding__risk-suggest">
            <p>
              High-risk case detected. We suggest starting with{" "}
              <strong>{suggestedCaregiver.name}</strong>.
            </p>
            <button
              type="button"
              className="hire-onboarding__cta hire-onboarding__cta--inline"
              disabled={busy}
              onClick={() => addPreference(suggestedCaregiver)}
            >
              Use suggested caregiver
            </button>
          </div>
        ) : null}
        <PreferredCaregiversPanel
          preferences={preferences}
          onRemove={removePreference}
          onMove={movePreference}
          onBook={() => {}}
          emptyHint="Browse caregivers and add them from the listing, or they appear here from your session."
        />
        {viewedCaregivers.length ? (
          <div className="hire-onboarding__browsed">
            <h3>From your browsing session</h3>
            <ul>
              {viewedCaregivers.map((c) => (
                <li key={c.id}>
                  <span>
                    {c.name}
                    {c.rating ? ` · ★ ${Number(c.rating).toFixed(1)}` : ""}
                  </span>
                  <button
                    type="button"
                    disabled={preferenceIds.has(String(c.id)) || preferences.length >= MAX_CAREGIVER_PREFERENCES}
                    onClick={() => addPreference(c)}
                  >
                    {preferenceIds.has(String(c.id)) ? "Added" : "Add"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {requireBooking ? (
          <button
            type="button"
            className="hire-onboarding__cta"
            disabled={busy || !preferences.length}
            onClick={confirmBooking}
          >
            {busy ? "Booking…" : "Book Now"}
          </button>
        ) : (
          <div className="hire-onboarding__actions">
            <button
              type="button"
              className="hire-onboarding__back"
              disabled={busy}
              onClick={() => {
                setStep(2);
                setStep2Phase("health");
              }}
            >
              Back
            </button>
            <button
              type="button"
              className="hire-onboarding__cta hire-onboarding__cta--inline"
              disabled={busy}
              onClick={preferences.length ? confirmBooking : finishSignupWithoutBooking}
            >
              {busy ? "Saving…" : preferences.length ? "Book with preferences" : "Continue"}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={rootClass}>
      <SignupProgress
        current={CUSTOMER_SIGNUP_STEPS.confirmation.index}
        total={CUSTOMER_SIGNUP_STEPS.confirmation.total}
      />
      <div className="hire-onboarding__head">
        <h2>{CUSTOMER_SIGNUP_STEPS.confirmation.title}</h2>
        <p>
          Reference <strong>#{bookingRef}</strong>. Status: <strong>Pending acceptance</strong>. Caregivers have up to 2
          hours to respond.
        </p>
        {careManagerAssigned === true ? (
          <p className="hire-onboarding__cm-notice">
            A care manager has been assigned to your booking. You can expect coordination support for next steps.
          </p>
        ) : careManagerAssigned === false ? (
          <p className="hire-onboarding__cm-notice hire-onboarding__cm-notice--muted">
            No care manager was available for assignment right now. You can proceed directly with your caregiver.
          </p>
        ) : null}
      </div>
      <ul className="hire-onboarding__summary">
        {preferences.map((p, i) => (
          <li key={p.id}>
            Preference {i + 1}: {p.name}
          </li>
        ))}
      </ul>
      <button type="button" className="hire-onboarding__cta" onClick={finishToDashboard}>
        Go to dashboard
      </button>
      </div>
      <ClinicalScreeningOutputModal
        open={showResultsModal}
        seniorName={senior.name}
        assessment={assessmentResult}
        caregivers={matchedCaregivers}
        matchesLoading={matchesLoading}
        matchCriteria={
          assessmentResult
            ? buildMarketplaceQueryFromElder({
                senior,
                assessment: assessmentResult,
                searchContext,
              }).criteria
            : null
        }
        customerEmail={customerEmail}
        onContinue={proceedAfterAssessment}
        onContinueAnyway={proceedAfterAssessment}
        onRetake={retakeAssessment}
        onAddCaregiver={addPreference}
        busy={busy}
      />
    </>
  );
}
